import { Project } from "projen/lib";
import { TurboRepo } from "../../turbo/turbo-repo";

/**
 * Fully-resolved build policy for the consuming project.
 *
 * The generated agent guidance around `pnpm build:all` used to assert
 * unconditionally that the command "requires the user to be
 * authenticated to AWS on the prod account used for Turborepo remote
 * caching (`readonlyaccess-prod-525259625215-us-east-1` profile)".
 * Both halves of that sentence were wrong for most consumers:
 *
 *   1. The AWS-auth requirement only exists when a Turborepo **remote
 *      cache** is configured. Consumers running a local cache only
 *      (`turbo.json` with just a `cacheDir`) need no credentials at
 *      all, and agents that believed otherwise aborted mid-flow —
 *      three lost-work incidents in `codedrifters/openhi-planning`.
 *   2. The profile name was this repository's own profile, baked
 *      verbatim into every consumer's generated text.
 *
 * This struct carries the two facts the rule renderers need, derived
 * from the project's actual {@link TurboRepo} configuration, so the
 * guidance is true for whichever consumer it renders into.
 *
 * @see resolveBuildPolicy
 */
export interface ResolvedBuildPolicy {
  /**
   * Whether a Turborepo **remote** cache is configured on the project.
   *
   * `false` means either there is no {@link TurboRepo} component at
   * all, or it was constructed without `remoteCacheOptions` — in both
   * cases `pnpm build:all` needs no AWS credentials and the generated
   * guidance must not claim otherwise.
   */
  readonly remoteCacheEnabled: boolean;

  /**
   * Local AWS profile name used to fetch the remote-cache endpoint and
   * token, taken from `remoteCacheOptions.profileName`.
   *
   * `undefined` whenever {@link remoteCacheEnabled} is `false`. Never
   * hard-code a profile name in rule content — read it from here so
   * each consumer's generated text names its own profile.
   */
  readonly awsProfileName?: string;
}

/**
 * Build policy for a project with no Turborepo remote cache — the
 * zero-config default. Rule renderers that receive this omit the
 * AWS-authentication guidance entirely rather than asserting a
 * credential requirement that does not exist.
 */
export const DEFAULT_BUILD_POLICY: ResolvedBuildPolicy = {
  remoteCacheEnabled: false,
};

/**
 * Derives the {@link ResolvedBuildPolicy} for a project by inspecting
 * its {@link TurboRepo} component.
 *
 * Auto-detection, not opt-in: `remoteCacheOptions` being `undefined`
 * *is* the "remote cache disabled" signal — `TurboRepo.renderRunArgs`
 * already branches on exactly the same condition when it decides
 * whether to emit `--api` / `--token` / `--team` flags. Consumers get
 * accurate guidance with no extra configuration.
 *
 * Call this lazily (at synthesis time), not from a constructor: the
 * `TurboRepo` component must already be attached to the project for
 * detection to succeed.
 */
export function resolveBuildPolicy(project: Project): ResolvedBuildPolicy {
  const remoteCacheOptions = TurboRepo.of(project)?.remoteCacheOptions;

  if (!remoteCacheOptions) {
    return DEFAULT_BUILD_POLICY;
  }

  return {
    remoteCacheEnabled: true,
    awsProfileName: remoteCacheOptions.profileName,
  };
}
